import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import useMediaQuery from '../utils/useMediaQuery';

// Inline SVG icons keep the nav looking modern (no emoji rendering inconsistencies)
const Icon = ({ name, active }) => {
  const stroke = active ? '#fbbf24' : '#94a3b8';
  const props = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  switch (name) {
    case 'globe':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </svg>
      );
    case 'stickers':
      return (
        <svg {...props}>
          <path d="M12 3l2.5 5.5L20 9l-4.2 3.8L17 19l-5-2.8L7 19l1.2-6.2L4 9l5.5-.5L12 3z" />
        </svg>
      );
    case 'quests':
      return (
        <svg {...props}>
          <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14" />
        </svg>
      );
    default:
      return null;
  }
};

const TABS = [
  { id: 'globe',    label: 'Globe'    },
  { id: 'stickers', label: 'Stickers' },
  { id: 'quests',   label: 'Quests'   },
];

/**
 * BottomNav — sleek, glass-backed persistent navigator.
 *
 * Props:
 *   activeTab       — 'globe' | 'stickers' | 'quests'
 *   onTabChange     — (tabId) => void
 *   stickerCount    — number shown as badge on Stickers tab
 *   questsDone      — number of completed quests (badge on Quests tab)
 */
export default function BottomNav({ activeTab, onTabChange, stickerCount = 0, questsDone = 0 }) {
  const badges = { stickers: stickerCount, quests: questsDone };
  const isMobile = useMediaQuery('(max-width: 640px)');
  const reduced = useReducedMotion();

  return (
    <nav style={{ ...s.nav, ...(isMobile ? s.navMobile : {}) }} aria-label="Main navigation">
      {TABS.map(tab => {
        const active = activeTab === tab.id;
        const badge  = badges[tab.id] || 0;
        return (
          <motion.button
            key={tab.id}
            style={{ ...s.tab, ...(isMobile ? s.tabMobile : {}) }}
            onClick={() => onTabChange(tab.id)}
            aria-current={active ? 'page' : undefined}
            aria-label={tab.label}
            whileTap={reduced ? undefined : { scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          >
            <span style={s.iconWrap}>
              {active && (
                <motion.span
                  layoutId="navPill"
                  style={s.pill}
                  transition={reduced ? { duration: 0 } : { type: 'spring', stiffness: 380, damping: 32 }}
                  aria-hidden="true"
                />
              )}
              <span style={s.iconInner}>
                <Icon name={tab.id} active={active} />
              </span>
              <AnimatePresence>
                {badge > 0 && (
                  <motion.span
                    key="badge"
                    style={s.badge}
                    aria-label={`${badge} ${tab.label}`}
                    initial={reduced ? { opacity: 0 } : { scale: 0, opacity: 0 }}
                    animate={reduced ? { opacity: 1 } : { scale: 1, opacity: 1 }}
                    exit={reduced ? { opacity: 0 } : { scale: 0, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 600, damping: 22 }}
                  >
                    {badge > 99 ? '99+' : badge}
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
            <span style={{ ...s.label, ...(active ? s.labelActive : {}) }}>
              {tab.label}
            </span>
          </motion.button>
        );
      })}
    </nav>
  );
}

const s = {
  nav: {
    position:        'fixed',
    bottom:          12,
    left:            '50%',
    transform:       'translateX(-50%)',
    width:           'min(460px, calc(100% - 24px))',
    display:         'flex',
    alignItems:      'stretch',
    background:      'rgba(8, 18, 38, 0.72)',
    backdropFilter:  'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border:          '1px solid rgba(255,255,255,0.10)',
    borderRadius:    20,
    boxShadow:       '0 18px 40px rgba(2, 6, 23, 0.45), inset 0 1px 0 rgba(255,255,255,0.08)',
    zIndex:          30,
    padding:         '6px',
    gap:             4,
    paddingBottom:   `calc(6px + env(safe-area-inset-bottom, 0px))`,
  },
  navMobile: {
    bottom: 8,
    width: 'calc(100% - 16px)',
    borderRadius: 18,
    padding: '5px',
    paddingBottom: `calc(5px + env(safe-area-inset-bottom, 0px))`,
  },

  tab: {
    flex:            1,
    display:         'flex',
    flexDirection:   'column',
    alignItems:      'center',
    justifyContent:  'center',
    gap:             3,
    padding:         '8px 0 6px',
    background:      'transparent',
    border:          'none',
    cursor:          'pointer',
    position:        'relative',
    minHeight:       54,
    borderRadius:    14,
    transition:      'background 180ms var(--ease-out)',
  },
  tabMobile: {
    minHeight: 50,
    padding: '7px 0 5px',
  },

  iconWrap: {
    position:        'relative',
    width:           34,
    height:          34,
    borderRadius:    12,
    display:         'grid',
    placeItems:      'center',
  },
  pill: {
    position:        'absolute',
    inset:           0,
    borderRadius:    12,
    background:      'linear-gradient(135deg, rgba(251,191,36,0.22), rgba(245,158,11,0.10))',
    boxShadow:       'inset 0 0 0 1px rgba(251,191,36,0.35), 0 6px 16px rgba(251,191,36,0.18)',
    zIndex:          0,
  },
  iconInner: {
    position:        'relative',
    zIndex:          1,
    display:         'grid',
    placeItems:      'center',
  },

  badge: {
    position:        'absolute',
    top:             -4,
    right:           -6,
    background:      '#ef4444',
    color:           '#fff',
    fontSize:        11,
    fontWeight:      700,
    borderRadius:    999,
    minWidth:        18,
    height:          18,
    display:         'grid',
    placeItems:      'center',
    padding:         '0 4px',
    lineHeight:      1,
    boxShadow:       '0 2px 8px rgba(239,68,68,0.45), 0 0 0 2px rgba(8, 18, 38, 0.95)',
    letterSpacing:   '-0.01em',
    zIndex:          2,
  },

  label: {
    fontSize:        14, // kid-readability floor (UI_REVIEW §5)
    fontWeight:      600,
    color:           '#94a3b8',
    letterSpacing:   '0.01em',
    transition:      'color 180ms var(--ease-out)',
  },
  labelActive: {
    color:           '#fbbf24',
    fontWeight:      700,
  },
};
